import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {HelpIcon} from '@/components/help-icon'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useCerrarRevision,
  useRevisionConcepto,
  type Revision,
  type RevisionCierre,
  type RevisionHallazgo,
} from '@/hooks/salud/medicina_laboral/use-conceptos'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly, formatInstantDate} from '@/lib/risk'

/** E1-H10. No hay rojo: el backend tiene dos veredictos, y «no coincide» solo lo dice una persona
 * al cerrar la revisión. */
const TONO: Record<string, 'green' | 'amber'> = {verde: 'green', amarillo: 'amber'}

/** Las formas buscadas van siempre, también si se encontró: dejan rehacer el resultado contra el papel. */
function Enfasis({hallazgo, sufijo}: {hallazgo: RevisionHallazgo; sufijo: string}) {
  const {t} = useTranslation()
  const [copiado, setCopiado] = useState(false)
  return (
    <li className="space-y-0.5" data-testid={`revision-enfasis-${sufijo}`}>
      <p className="text-sm">
        <Tag tone={hallazgo.encontrado ? 'green' : 'amber'} className="mr-2">
          {t(`revision.enfasis.${hallazgo.encontrado ? 'encontrado' : 'faltante'}`)}
        </Tag>
        {hallazgo.enfasis}
      </p>
      <p className="text-xs text-muted-foreground">{t('revision.busque', {terminos: hallazgo.buscado.join(', ')})}</p>
      {hallazgo.encontrado && hallazgo.fragmento && (
        <p className="text-xs text-muted-foreground" data-testid={`revision-donde-${sufijo}`}>
          {hallazgo.pagina
            ? t('revision.encontradoEnPagina', {fragmento: hallazgo.fragmento, pagina: hallazgo.pagina})
            : t('revision.encontradoEn', {fragmento: hallazgo.fragmento})}
        </p>
      )}
      {/* Pedirle a la IPS que amplíe está diferido (§9): mientras tanto se copia el texto. */}
      {!hallazgo.encontrado && (
        <Button
          size="sm"
          variant="ghost"
          data-testid={`revision-copiar-${sufijo}`}
          onClick={() => {
            void navigator.clipboard
              ?.writeText(
                t('revision.textoParaIps', {enfasis: hallazgo.enfasis, terminos: hallazgo.buscado.join(', ')})
              )
              .then(() => setCopiado(true))
          }}>
          {t(`revision.acciones.${copiado ? 'copiado' : 'copiarParaIps'}`)}
        </Button>
      )}
    </li>
  )
}

/** Sin solicitud registrada nunca se dice «lo que usted pidió», sino «lo que el cargo haría esperar». */
function BaseDeLaComparacion({revision}: {revision: Revision}) {
  const {t} = useTranslation()
  if (revision.base_comparacion === 'perfil_hoy') {
    // D-R fija «versión del [fecha]»: el número de versión no dice qué tan vieja es la foto.
    const clave = revision.perfil_confirmado_en ? 'revision.base.perfil_hoy' : 'revision.base.perfilSinFecha'
    return (
      <p className="text-xs text-muted-foreground" data-testid={`revision-base-${revision.examen_id}`}>
        {t(clave, {
          cargo: revision.cargo ?? '',
          fecha: revision.perfil_confirmado_en ? formatDateOnly(revision.perfil_confirmado_en) : '',
        })}
      </p>
    )
  }
  return (
    <p className="text-xs text-muted-foreground" data-testid={`revision-base-${revision.examen_id}`}>
      {t(`revision.base.${revision.base_comparacion}`)}
      {revision.por_confirmar && ` · ${t('revision.base.porConfirmar')}`}
    </p>
  )
}

function Elementos({revision}: {revision: Revision}) {
  const {t} = useTranslation()
  const noVerificables = revision.elementos.filter((elemento) => !elemento.verificable)
  return (
    <div className="space-y-1" data-testid={`revision-elementos-${revision.examen_id}`}>
      <p className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
        {t('revision.elementos.titulo')}
      </p>
      <ul className="flex flex-wrap gap-1">
        {revision.elementos.map((elemento) => (
          <li key={elemento.clave}>
            <Tag tone={elemento.encontrado ? 'green' : 'neutral'} data-testid={`revision-elemento-${elemento.clave}`}>
              {t(`revision.elementos.${elemento.clave}`)}
              {elemento.registrado && ` · ${t('revision.elementos.registrado')}`}
            </Tag>
          </li>
        ))}
      </ul>
      {revision.elementos_faltantes.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid={`revision-faltantes-${revision.examen_id}`}>
          {t('revision.elementos.faltan', {
            claves: revision.elementos_faltantes.map((clave) => t(`revision.elementos.${clave}`)).join(', '),
          })}
        </p>
      )}
      {/* «No pude verificar» nunca se escribe como «no encontré»: sin base, el elemento no se buscó. */}
      {noVerificables.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid={`revision-no-verificables-${revision.examen_id}`}>
          {t('revision.elementos.noVerificable', {
            claves: noVerificables.map((elemento) => t(`revision.elementos.${elemento.clave}`)).join(', '),
          })}
        </p>
      )}
    </div>
  )
}

function Cierre({
  revision,
  puedeCerrar,
  onCerrar,
  cerrando,
}: {
  revision: Revision
  puedeCerrar: boolean
  onCerrar: (cierre: RevisionCierre) => void
  cerrando: boolean
}) {
  const {t} = useTranslation()
  if (revision.cierre) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`revision-cierre-${revision.examen_id}`}>
        {revision.cerrado_automaticamente
          ? t('revision.cierre.automatico', {fecha: revision.cerrado_en ? formatInstantDate(revision.cerrado_en) : ''})
          : t(`revision.cierre.${revision.cierre}`, {
              nombre: revision.cerrado_por_nombre ?? t('revision.cierre.operadorPlataforma'),
              fecha: revision.cerrado_en ? formatInstantDate(revision.cerrado_en) : '',
            })}
      </p>
    )
  }
  return (
    <div className="space-y-2">
      <Tag tone="amber" data-testid={`revision-pendiente-${revision.examen_id}`}>
        {t('revision.pendiente')}
      </Tag>
      {puedeCerrar && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={cerrando}
            data-testid={`revision-cerrar-coincide-${revision.examen_id}`}
            onClick={() => onCerrar('coincide')}>
            {t('revision.acciones.coincide')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={cerrando}
            data-testid={`revision-cerrar-no-coincide-${revision.examen_id}`}
            onClick={() => onCerrar('no_coincide')}>
            {t('revision.acciones.noCoincide')}
          </Button>
        </div>
      )}
    </div>
  )
}

/** D-R: el título no dice «lo que usted pidió» cuando la base es el perfil. */
function tituloDe(items: Revision[]): string {
  if (items.length > 0 && items.every((revision) => revision.base_comparacion === 'perfil_hoy')) {
    return 'revision.tituloPerfil'
  }
  if (items.some((revision) => revision.base_comparacion === 'perfil_hoy')) return 'revision.tituloMixto'
  return 'revision.titulo'
}

export function RevisionConcepto({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  // Cerrar es de firmante: la capacidad la computa el backend (licencia y rol), la UI no la rederiva.
  const capabilities = useCapabilities(businessId)
  const revisiones = useRevisionConcepto(businessId, employeeId)
  const cerrar = useCerrarRevision(businessId, employeeId)
  const [error, setError] = useState<string | null>(null)

  if (revisiones.isPending) return <Skeleton className="h-32 w-full" data-testid="revision-cargando" />
  if (revisiones.isError) {
    return (
      <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="revision-error">
        {t('revision.errors.cargaFallida')}
      </p>
    )
  }
  const datos = revisiones.data
  const puedeCerrar = capabilities.sgsst_firmar && !godReadOnly

  return (
    <section className="surface space-y-4 rounded-lg p-4" data-testid="revision-concepto">
      <div className="flex flex-wrap items-center gap-1.5">
        <h3 className="text-sm font-semibold" data-testid="revision-titulo">
          {t(tituloDe(datos.items))}
        </h3>
        <HelpIcon label={t('common.help.iconLabelFor', {campo: t(tituloDe(datos.items))})} testId="revision-encabezado">
          <p>{t('revision.encabezado')}</p>
          <p className="mt-1.5">{t('revision.quienDetermina')}</p>
        </HelpIcon>
      </div>
      {error && (
        <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="revision-error-accion">
          {error}
        </p>
      )}
      {datos.avisos_total > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="revision-avisos-total">
          {t('revision.avisosTotal', {count: datos.avisos_total})}
        </p>
      )}
      {datos.items.length === 0 && datos.sin_comparacion.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="revision-vacia">
          {t('revision.vacia')}
        </p>
      )}
      <ul className="space-y-4">
        {datos.items.map((revision) => (
          <li key={revision.id} className="space-y-2 border-t pt-3" data-testid={`revision-item-${revision.examen_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Tag tone={TONO[revision.veredicto]} data-testid={`revision-veredicto-${revision.examen_id}`}>
                {t(`revision.veredicto.${revision.veredicto}`)}
              </Tag>
              <BaseDeLaComparacion revision={revision} />
            </div>
            {revision.avisos.map((aviso) => (
              <p key={aviso} className="text-xs text-muted-foreground" data-testid={`revision-aviso-${aviso}`}>
                {t(`revision.avisos.${aviso}`)}
                {aviso === 'sin_solicitud' && (
                  <Link className="ml-1 underline" to={`/businesses/${businessId}/solicitudes-ips`}>
                    {t('revision.avisos.irASolicitudes')}
                  </Link>
                )}
              </p>
            ))}
            {revision.hallazgos.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid={`revision-sin-enfasis-${revision.examen_id}`}>
                {t('revision.sinEnfasis')}
              </p>
            ) : (
              <ul className="space-y-2">
                {revision.hallazgos.map((hallazgo) => (
                  <Enfasis
                    key={hallazgo.enfasis}
                    hallazgo={hallazgo}
                    sufijo={`${revision.examen_id}-${hallazgo.enfasis}`}
                  />
                ))}
              </ul>
            )}
            <Elementos revision={revision} />
            <Cierre
              revision={revision}
              puedeCerrar={puedeCerrar}
              cerrando={cerrar.isPending}
              onCerrar={(cierre) => {
                setError(null)
                cerrar.mutate(
                  {revisionId: revision.id, cierre},
                  {onError: (fallo) => setError(translateApiError(fallo))}
                )
              }}
            />
          </li>
        ))}
      </ul>
      {datos.sin_comparacion.map((sin) => (
        <div key={sin.examen_id} className="space-y-1 border-t pt-3" data-testid={`revision-sin-base-${sin.examen_id}`}>
          <p className="text-sm" data-testid={`revision-sin-comparacion-motivo-${sin.examen_id}`}>
            {t(sin.avisos.includes('sin_base') ? 'revision.sinBase' : 'revision.sinTexto')}
          </p>
          <div className="flex flex-wrap gap-3 text-xs">
            <Link className="underline" to={`/businesses/${businessId}/solicitudes-ips`}>
              {t('revision.avisos.irASolicitudes')}
            </Link>
            <Link
              className="underline"
              to={
                sin.position_id
                  ? `/businesses/${businessId}/sgsst/perfiles-cargo/${sin.position_id}`
                  : `/businesses/${businessId}/sgsst/perfiles-cargo`
              }>
              {sin.cargo ? t('revision.irAlPerfilDe', {cargo: sin.cargo}) : t('revision.irAlPerfil')}
            </Link>
          </div>
        </div>
      ))}
    </section>
  )
}
